/**
 * 🎯 HEARTBEATS PROVIDER - Le cerveau central
 * 
 * Context global qui gère :
 * - État utilisateur & profil
 * - Connexion temps réel (WebSocket)
 * - IA Wingman (Pixie la commandante)
 * - Notifications & événements
 * 
 * Architecture Mozart-Jobs : Propre, scalable, magnifique.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

// 🎯 Context principal
const HeartbeatsContext = createContext(null);

// 🧚‍♀️ PIXIE - La commandante de la cabine
export const PIXIE = {
  name: 'Pixie',
  emoji: '🚀',
  title: 'Ta commandante de bord',
  personality: 'Malicieuse, bienveillante, et toujours là pour toi',
  colors: {
    primary: '#EC4899', // Pink
    secondary: '#8B5CF6', // Purple
    glow: 'rgba(236, 72, 153, 0.5)'
  }
};

// 💫 Messages de Pixie
export const PIXIE_MESSAGES = {
  welcome: [
    "Hey toi ! 🚀 Je suis Pixie, ta commandante de bord ! Prêt à faire des étincelles dans les étoiles ? ✨",
    "Coucou ! 💜 Pixie à ton service ! L'amour est dans l'air, je le sens... 🌸",
    "Bienvenue dans mon monde magique ! 🧚‍♀️ Je vais t'aider à trouver l'amour ! ✨"
  ],
  icebreakers: [
    "Psst ! 🧚‍♀️ Essaie : 'J'ai remarqué que tu aimes {passion}... Moi aussi !' ",
    "Ma baguette me dit... 💫 Parle-lui de {topic}, ça va matcher !",
    "Petit sort d'amour : 🪄 Pose une question sur sa bio, ça marche toujours !"
  ],
  encouragement: [
    "T'inquiète pas ! 🧚‍♀️ Même Cendrillon a dû attendre minuit... 💜",
    "La magie prend du temps ! ✨ Ton prince/princesse arrive bientôt !",
    "Je sens de bonnes vibrations... 🌟 Continue comme ça !"
  ],
  match: [
    "BOOM ! 💥✨ Ma baguette a fait des étincelles ! T'as un MATCH ! 🧚‍♀️",
    "OMG OMG OMG ! 🧚‍♀️💜 Y'a de l'amour dans l'air ! MATCH ! ✨",
    "*agite sa baguette* 🪄 TADAAA ! Un match magique ! 💫"
  ],
  wingman: [
    "🧚‍♀️ Petit conseil : {advice}",
    "*chuchote* 💫 Tu devrais {advice}",
    "Ma magie me dit... 🪄 {advice}"
  ]
};

// 🎯 Provider principal
export function HeartbeatsProvider({ children, customer, profile: initialProfile }) {
  // 👤 État utilisateur
  const [profile, setProfile] = useState(initialProfile);
  const [isOnline, setIsOnline] = useState(true);
  const [lastSeen, setLastSeen] = useState(new Date());
  
  // 💜 État Heartbeats
  const [matches, setMatches] = useState([]);
  const [crushes, setCrushes] = useState([]);
  const [conversations, setConversations] = useState({});
  const [notifications, setNotifications] = useState([]);
  
  // 🚀 Pixie - État de la commandante
  const [pixieMessage, setPixieMessage] = useState(null);
  const [pixieVisible, setPixieVisible] = useState(false);
  const [wingmanMode, setWingmanMode] = useState(true);
  
  // 📸 Stories
  const [stories, setStories] = useState([]);
  const [myStory, setMyStory] = useState(null);
  
  // 🎮 Jeux
  const [activeGame, setActiveGame] = useState(null);
  
  // 🙈 Blind Date
  const [blindDateActive, setBlindDateActive] = useState(false);
  const [blindDateMatch, setBlindDateMatch] = useState(null);
  
  // 🔌 WebSocket ref
  const wsRef = useRef(null);
  
  // 🧚‍♀️ Faire apparaître Pixie avec un message
  const showPixie = useCallback((type = 'encouragement', customMessage = null) => {
    const messages = PIXIE_MESSAGES[type] || PIXIE_MESSAGES.encouragement;
    const message = customMessage || messages[Math.floor(Math.random() * messages.length)];
    
    setPixieMessage(message);
    setPixieVisible(true);
    
    // Auto-hide après 5 secondes
    setTimeout(() => setPixieVisible(false), 5000);
  }, []);
  
  // 🪄 Générer un icebreaker avec Pixie
  const getIcebreaker = useCallback((targetProfile) => {
    if (!targetProfile) return null;
    
    const passions = targetProfile.passions ? Object.values(targetProfile.passions).flat() : [];
    const randomPassion = passions[Math.floor(Math.random() * passions.length)] || 'la vie';
    
    const templates = [
      `Hey ! J'ai vu que t'aimais ${randomPassion}... Raconte-moi ton meilleur souvenir lié à ça ! 😊`,
      `${randomPassion} ? Trop cool ! Moi aussi je kiffe ça ! C'est quoi ton truc préféré ? 🔥`,
      `J'ai tilté sur ${randomPassion} dans ton profil... On dirait qu'on a des points communs ! 💜`,
      `Dis-moi tout sur ${randomPassion} ! J'adore découvrir de nouvelles passions 🌟`,
      `${randomPassion} = instant connexion ! Tu fais ça depuis longtemps ? ✨`
    ];
    
    return templates[Math.floor(Math.random() * templates.length)];
  }, []);
  
  // 💫 Conseil Wingman en temps réel
  const getWingmanAdvice = useCallback((context) => {
    const advices = {
      slow_response: "Relance avec une question fun ! Genre 'Tu préfères...' 🎮",
      no_response: "Envoie un GIF ou un emoji rigolo pour détendre l'atmosphère ! 😄",
      going_well: "Ça matche bien ! Propose un café ou un verre ! ☕🍷",
      first_message: "Évite le 'salut ça va'. Sois original ! Parle de sa bio ! 💜",
      awkward: "Change de sujet ! Parle de ses passions ou pose un 'Tu préfères' 🎯"
    };
    
    return advices[context] || advices.first_message;
  }, []);
  
  // 📤 Ajouter une notification
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [{
      id: Date.now(),
      timestamp: new Date(),
      read: false,
      ...notification
    }, ...prev].slice(0, 50));
  }, []);
  
  // 🎯 Valeur du context
  const value = {
    // User
    customer,
    profile,
    setProfile,
    isOnline,
    setIsOnline,
    
    // Heartbeats
    matches,
    setMatches,
    crushes,
    setCrushes,
    conversations,
    setConversations,
    notifications,
    addNotification,
    
    // 🧚‍♀️ Pixie
    pixie: PIXIE,
    pixieMessage,
    pixieVisible,
    showPixie,
    setPixieVisible,
    wingmanMode,
    setWingmanMode,
    getIcebreaker,
    getWingmanAdvice,
    
    // 📸 Stories
    stories,
    setStories,
    myStory,
    setMyStory,
    
    // 🎮 Games
    activeGame,
    setActiveGame,
    
    // 🙈 Blind Date
    blindDateActive,
    setBlindDateActive,
    blindDateMatch,
    setBlindDateMatch,
    
    // WebSocket
    wsRef
  };
  
  return (
    <HeartbeatsContext.Provider value={value}>
      {children}
    </HeartbeatsContext.Provider>
  );
}

// 🎯 Hook pour utiliser le context
export function useHeartbeats() {
  const context = useContext(HeartbeatsContext);
  if (!context) {
    throw new Error('useHeartbeats must be used within HeartbeatsProvider');
  }
  return context;
}

export default HeartbeatsProvider;
